import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Select, Space, Table, message } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { TableDownload } from "../../components/TableDownload";
import { ColumnPicker, useColumnPicker } from "../../components/ColumnPicker";
import { PrintFilters } from "../../components/PrintFilters";
import { api, errorMessage } from "../../helpers/api";
import { useProjectsQuery } from "../../helpers/queries";
import { REPORT_DEFS, type Row } from "./reportDefs";

// A report that does not exist has no columns; the hook below still has to be called.
const NO_COLUMNS: (typeof REPORT_DEFS)[string]["columns"] = [];

export default function ReportPage() {
  const { key = "" } = useParams();
  const def = REPORT_DEFS[key];
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState<number>();
  const { data: projects = [] } = useProjectsQuery();
  // Keyed by report, not by page: every report on this one page keeps its own column choice.
  const cols = useColumnPicker(`report:${key}`, def?.columns ?? NO_COLUMNS);

  // Some endpoints answer per project only: calling them without one is a 400, not an
  // empty report, so wait for the filter rather than rendering a misleading "No data".
  const awaitingProject = !!def?.requiresProject && !projectId;

  useEffect(() => {
    if (!def) return;
    if (awaitingProject) return setRows([]);
    setLoading(true);
    api
      .get<Row[]>(def.endpoint, { params: def.filter === "project" ? { projectId } : undefined })
      .then((res) => setRows(res.data ?? []))
      .catch((err) => {
        setRows([]);
        message.error(errorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [def, projectId, awaitingProject]);

  if (!def) return <PageHeader title="Report not found" />;

  const project = projects.find((p) => p.id === projectId);
  // One description of the view, read by both the export header and the printed page.
  const filters = def.filter === "project" ? { project: project ? `${project.code} — ${project.name}` : "All projects" } : undefined;

  return (
    <div>
      <PageHeader
        title={def.title}
        subtitle={def.desc}
        extra={
          <Space>
            <ColumnPicker state={cols} />
            <TableDownload
              name={def.title}
              // The export is the table as shown, so a column hidden here is out of the file.
              columns={cols.visible}
              dataSource={rows}
              disabled={!rows.length}
              filters={filters}
              // The caveat under the table travels with the file: a number read without it is misread.
              extraHeaders={def.note ? [["Note", def.note]] : []}
              beforeDownload={() => (awaitingProject ? "Select a project first." : null)}
            />
          </Space>
        }
      />
      {def.filter === "project" && (
        <div className="glut-filter-bar">
          <Select
            allowClear
            placeholder={def.requiresProject ? "Select a project" : "All projects"}
            style={{ width: 260 }}
            value={projectId}
            onChange={setProjectId}
            options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
        </div>
      )}
      <PrintFilters filters={filters} />
      {def.note && <p style={{ color: "rgba(0,0,0,0.45)", marginTop: 0 }}>{def.note}</p>}
      <Table
        rowKey={(_, i) => String(i)}
        loading={loading}
        dataSource={rows}
        columns={cols.visible}
        scroll={{ x: "max-content" }}
        locale={awaitingProject ? { emptyText: "Select a project to run this report." } : undefined}
      />
    </div>
  );
}
